/**
 * Shared conversation-store registry — the one maintainable session layer for
 * surfaces OTHER than the main chat.
 *
 * The main chat's durability comes from its module-level global `useChatbotStore`:
 * the store outlives the panel UI, so the in-flight stream keeps accumulating even
 * while the panel is unmounted, and remounting re-reads it. Background board runs get
 * durability differently — the run is server-side, so a remount re-tails its Redis
 * stream (`useAgentRunStream`). Ask cards are the third case: the send is
 * client-initiated (no server-side run to re-tail), and the card modal unmounts when
 * you flip to another card — so without a home ABOVE the component, the in-flight
 * reply is lost on flip (observed: "my thinking response was gone").
 *
 * This registry gives ask cards the same guarantee the main chat has: one
 * `createChatbotStore()` instance per conversation id, cached at module scope so it
 * survives unmount/remount. The store still owns everything (send, streaming,
 * in-flight tracking, S3 hydration, resume, reconnect) — exactly as it does for chat
 * and background runs; the registry only keeps the instance alive. Consumed via
 * `<ChatbotStoreProvider store={getSharedConversationStore(id)}>`.
 */
import { createChatbotStore, type ChatbotStoreApi } from './chatbotStore';
import { executiveResourceKey, ExecutiveResourceMap } from './executiveSession';
import { clearConversationScrollPosition } from './conversationScrollPositions';

const stores = new ExecutiveResourceMap<ChatbotStoreApi>((store) => {
  const conversationId = store.getState().conversationId;
  // Invalidate detached callbacks without cancelling the server's ongoing turn.
  store.setState(store.getInitialState(), true);
  if (conversationId) clearConversationScrollPosition(conversationId);
});

export interface SharedConversationStoreOptions {
  /**
   * Force code-mode Leo for this conversation. The store's `sendMessage` derives
   * `agent_architecture` from `codeModeEnabled` — with it off it sends `'classic'`,
   * which would bypass the backend's memo→code-mode default (that default only fires
   * on an EMPTY architecture). Ask cards run read-only code-mode Leo, so they seed
   * this true, reproducing the pre-migration behavior where MemoChat sent no
   * architecture and the backend defaulted `conv_memo_*` ids to `'code'`. The backend
   * eligibility gate still governs activation (a non-opted-in account falls back to
   * classic + its memo paradigm), so this never forces the sandbox on an ineligible
   * account.
   */
  codeMode?: boolean;
  /**
   * Mark every send from this store as a READ-ONLY ask-card send (adds `ask_card` to
   * the /stream body). The backend keys its read-only code-mode surface on this flag,
   * NOT the conv id — so this instance is read-only, but a "Continue in chat"
   * continuation of the same conversation in the global panel (which never sets this)
   * runs full Leo.
   */
  askCardReadOnly?: boolean;
}

/**
 * The persistent store instance for a conversation id, created on first use and
 * reused thereafter. Same id across mounts → same instance → the in-flight stream
 * survives an unmount/remount.
 */
export function getSharedConversationStore(
  conversationId: string,
  opts?: SharedConversationStoreOptions,
): ChatbotStoreApi {
  let store = stores.get(executiveResourceKey(conversationId));
  if (!store) {
    store = createChatbotStore(conversationId);
    const seed: Partial<{ codeModeEnabled: boolean; askCardReadOnly: boolean }> = {};
    if (opts?.codeMode) seed.codeModeEnabled = true;
    if (opts?.askCardReadOnly) seed.askCardReadOnly = true;
    if (Object.keys(seed).length) store.setState(seed);
    stores.set(executiveResourceKey(conversationId), store);
  }
  return store;
}

/**
 * The cached store instance for a conversation id, WITHOUT creating one. This is
 * the lookup `chatbotApi` uses to route store mutations to the store actually
 * running a stream: every panel chat / ask card streams through a registry
 * instance, so a hit is authoritative; a miss means the stream belongs to a
 * surface still on the global `useChatbotStore` (the caller's fallback).
 */
export function peekSharedConversationStore(
  conversationId: string,
): ChatbotStoreApi | undefined {
  return stores.get(executiveResourceKey(conversationId));
}

/**
 * Drop a conversation's cached store. Call when a surface is done with it (e.g. an
 * ask card resolved + archived) so instances don't accumulate for the session. Safe
 * to call for an unknown id. NOT called on a mere unmount — that's the whole point of
 * the registry (the instance must outlive the component to survive a card flip).
 */
export function releaseSharedConversationStore(conversationId: string): void {
  stores.delete(executiveResourceKey(conversationId));
  // The conversation's ephemeral view state rides the same lifetime — clear it
  // from the single release choke point so no eviction path can leak it.
  clearConversationScrollPosition(conversationId);
}

/** Test seam only — clear the registry between cases. */
export function _resetSharedConversationStoresForTest(): void {
  stores.clear();
}

/**
 * Iterate every cached per-conversation store instance. Used by the global
 * store's session-level setters (code-mode opt-in, Slack simulation) to fan
 * a change out to all open chats so their `sendMessage` picks up the new
 * value without a page refresh.
 */
export function forEachSharedConversationStore(
  visit: (store: ChatbotStoreApi, conversationId: string) => void,
): void {
  stores.forEach((store, key) => {
    const cid = key.startsWith('["executive",') ? JSON.parse(key)[2] as string : key;
    visit(store, cid);
  });
}
